import { fetchQuery } from '@sequencehq/api'
import {
  type Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dashboard/v99990101'

import { apiQueryClient } from 'features/api/apiQueryClient'
import type {
  DependencyFetcher,
  DependencyResolutionRunOutcome,
  IndeterminateReason,
  ResolutionContext,
  SimulationDependency
} from '../../simulation.types'
import { getTaskDependencies } from '../getTaskDependencies'
import { parseTaskOutputReference } from '../parseTaskOutputReference'
import { resolveTaskData } from '../resolveTaskData'
import { invocationBody } from './invocationBody'
import { staleTimeFor } from './staleTimeFor'

type Body = Dashboardv99990101Api.PostInvokeTaskExecutor.Body
type TaskExecutorResult =
  Dashboardv99990101Api.PostInvokeTaskExecutor.TaskExecutorResult

/**
 * One task's fetch: what came back, that we left it alone, or why we stopped.
 *
 * "Skipped" is a task we deliberately didn't run, because something it reads is
 * allowed to be missing. It publishes nothing, so a route depending on it reads
 * undefined rather than the run reporting a failure.
 */
type DependencyOutcome =
  | { output: Record<string, unknown> }
  | { skipped: true }
  | { reason: IndeterminateReason }

/** What every task in one round of fetching needs, gathered once up front. */
type ResolveRun = {
  byReference: Record<string, SimulationDependency>
  context: ResolutionContext
  useCacheValueFor: string[]
  refresh: string[] | undefined
  fetchDependency: DependencyFetcher
}

/**
 * Fetches everything the conditions read.
 *
 * Some tasks are fetched with a value that came off another one, so we wait for
 * those and start everything else at once. A task read by two others gets walked
 * twice, but `fetchQuery` keys on the request body and hands both the same
 * answer, so it's still one trip to the backend.
 *
 *   getQuote --> getCustomer     getQuote and getUser both start at once;
 *   getUser                      getCustomer waits for getQuote's customerId
 *
 * All or nothing for now: one task we can't fetch and everything comes back
 * unresolved. Skipping just the affected route is a job for evaluation. The
 * exception is a task reading an optional dependency - see `DependencyOutcome`.
 */
export const resolveDependencies = async ({
  dependencies,
  workflowInput,
  useCacheValueFor = [],
  refresh,
  fetchDependency = fetchDependencyOutput
}: {
  dependencies: SimulationDependency[]
  workflowInput: Record<string, unknown>
  useCacheValueFor?: string[]
  /** Tasks to go and ask about again. Everything else comes off the cache. */
  refresh?: string[]
  fetchDependency?: DependencyFetcher
}): Promise<DependencyResolutionRunOutcome> => {
  const run: ResolveRun = {
    byReference: dependencies.reduce<Record<string, SimulationDependency>>(
      (acc, dependency) => ({
        ...acc,
        [dependency.task.taskReferenceName]: dependency
      }),
      {}
    ),
    context: { workflowInput, taskOutputs: {} },
    useCacheValueFor,
    refresh,
    fetchDependency
  }

  // Everything below either returns a reason or is caught around the fetch, so
  // nothing should throw. This is exported though, and a task shaped in a way
  // we didn't expect shouldn't take the caller down with it.
  const outcomes = await Promise.all(
    dependencies.map(dependency =>
      resolveTask({
        taskReferenceName: dependency.task.taskReferenceName,
        ancestors: [],
        run
      })
    )
  ).catch(() => undefined)

  if (!outcomes) return { reason: 'MALFORMED_WORKFLOW_DEFINITION' }

  const failed = outcomes.find(isFailure)
  if (failed) return { reason: failed.reason }

  return { taskOutputs: run.context.taskOutputs }
}

/**
 * Fetches one task, once everything it reads from has been fetched. Writes what
 * came back onto the run's context, so a task waiting on this one finds it
 * there.
 */
const resolveTask = async ({
  taskReferenceName,
  ancestors,
  run
}: {
  taskReferenceName: string
  ancestors: string[]
  run: ResolveRun
}): Promise<DependencyOutcome> => {
  // `ancestors` is the chain of tasks we came through to get here, so a task
  // pointing back into its own chain can never be fetched and we stop there. A
  // task reached from somewhere else is just two tasks reading the same thing,
  // and carries on as normal.
  if (ancestors.includes(taskReferenceName)) {
    return { reason: 'MALFORMED_WORKFLOW_DEFINITION' }
  }

  const dependency = run.byReference[taskReferenceName]
  if (!dependency) return { reason: 'UNSUPPORTED_DEPENDENCY' }

  const prerequisites = await Promise.all(
    getTaskDependencies(dependency.task).map(reference =>
      resolveTask({
        taskReferenceName: reference,
        ancestors: [...ancestors, taskReferenceName],
        run
      })
    )
  )

  const failed = prerequisites.find(isFailure)
  if (failed) return failed

  // We only ever ask for a task whose value this one reads, so a task we didn't
  // run leaves this one with nothing to run on. It follows the same way down.
  if (prerequisites.some(isSkipped)) return { skipped: true }

  const resolved = resolveTaskData({
    task: dependency.task,
    context: run.context
  })
  if ('unresolved' in resolved) {
    return readsAnOptionalDependency({ reference: resolved.unresolved, run })
      ? { skipped: true }
      : { reason: 'UNRESOLVED_REFERENCE' }
  }

  const body = invocationBody({ task: dependency.task, data: resolved.data })
  if (!body) return { reason: 'UNSUPPORTED_DEPENDENCY' }

  const response = await invoke({
    fetchDependency: run.fetchDependency,
    body,
    staleTime: staleTimeFor({
      dependency,
      useCacheValueFor: run.useCacheValueFor,
      refresh: run.refresh
    })
  })

  if (response?.status !== 'COMPLETED') return { reason: 'DEPENDENCY_FAILED' }

  run.context.taskOutputs[taskReferenceName] = response.outputData

  return { output: response.outputData }
}

/**
 * Actually goes and asks the backend to run a task. The whole request body is
 * the cache key, so asking the same executor for a different quote is a separate
 * entry and neither answer stands in for the other.
 */
const fetchDependencyOutput: DependencyFetcher = ({ body, staleTime }) =>
  fetchQuery(
    apiQueryClient,
    dashboardv99990101Client.postInvokeTaskExecutor,
    body,
    { staleTime }
  )

/** A request that fails is a dependency we couldn't get, not an error to raise. */
const invoke = async ({
  fetchDependency,
  body,
  staleTime
}: {
  fetchDependency: DependencyFetcher
  body: Body
  staleTime: number
}): Promise<TaskExecutorResult | undefined> => {
  try {
    return (await fetchDependency({ body, staleTime })) ?? undefined
  } catch {
    return undefined
  }
}

/**
 * Whether the reference we couldn't fill in points at a task that's allowed to
 * answer with nothing. Only a `${task.output.*}` reference can - a workflow
 * input we weren't given, or a task outside the analysed set, still stops the
 * run.
 */
const readsAnOptionalDependency = ({
  reference,
  run
}: {
  reference: string
  run: ResolveRun
}): boolean => {
  const parsed = parseTaskOutputReference(reference)
  if (parsed.kind !== 'task') return false

  return Boolean(run.byReference[parsed.taskReferenceName]?.isOptional)
}

const isFailure = (
  outcome: DependencyOutcome
): outcome is { reason: IndeterminateReason } => 'reason' in outcome

const isSkipped = (outcome: DependencyOutcome): boolean => 'skipped' in outcome
